import { Link } from 'react-router-dom';
import { Check, ArrowRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever free',
    features: [
      { text: '200 credits per month', check: true },
      { text: '10 PI Checks per day', check: true },
      { text: 'Basic message rewrite', check: true },
      { text: 'PI Craft: 5 casts/day', check: true },
      { text: '3 AI platforms (ChatGPT, Claude, Gemini)', check: true },
      { text: 'Grammar & clarity check', check: true },
      { text: 'Chrome Extension', check: true },
    ],
    cta: 'Get Started Free',
    ctaLink: '/signup',
    external: false,
    style: 'gradient',
    enabled: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$8',
    period: '/month',
    features: [
      { text: '1,000 PI Checks per day', check: true },
      { text: 'All 12 outcome modes', check: true },
      { text: 'All 8 relationship contexts', check: true },
      { text: '5 casts per day', check: true },
      { text: 'Token Optimizer', check: true },
      { text: 'All 6 AI platforms', check: true },
      { text: 'PI Cast: 10/day', check: true },
    ],
    cta: 'Coming Soon',
    ctaLink: '#',
    external: false,
    style: 'outline',
    enabled: false,
  },
  {
    id: 'max',
    name: 'Max',
    price: '$18',
    period: '/month',
    badge: 'COMING SOON',
    highlighted: true,
    features: [
      { text: 'Unlimited PI Checks', check: true },
      { text: 'Full PI Craft', check: true },
      { text: 'Unlimited casts', check: true },
      { text: 'PI Cast — all 20 categories', check: true },
      { text: 'PI Loop — auto-refinement', check: true },
      { text: 'All professional outcome templates', check: true },
      { text: 'PI Store access (buy + sell)', check: true },
      { text: 'Priority AI', check: true },
    ],
    cta: 'Coming Soon',
    ctaLink: '#',
    external: false,
    style: 'gradient',
    enabled: false,
  },
];

const faqs = [
  { q: 'Do I need an account to use SendPI?', a: 'You can try SendPI for free without an account (2 tries). Creating a free account gives you 200 credits per month and 10 PI Checks per day.' },
  { q: 'Is my message content stored?', a: 'Never. Only behavioral metadata (tone choices, risk scores). Zero message content stored. Privacy is enforced at the code level.' },
  { q: 'What is a "cast"?', a: 'A cast is a precision AI instruction generated by SendPI. Instead of writing a vague prompt, you state your outcome and SendPI generates the optimal instruction for your target AI platform.' },
  { q: 'What AI platforms does SendPI support?', a: 'ChatGPT, Claude, Gemini, Perplexity, MS Copilot, and Cursor. More platforms are added regularly.' },
  { q: 'Can I cancel anytime?', a: 'Yes. Cancel anytime with no penalties. Your account reverts to the Free tier immediately.' },
  { q: 'What languages are supported?', a: 'Currently English only. More languages are coming soon.' },
  { q: 'When will paid plans be available?', a: 'Pro and Max plans will launch after the pilot program concludes. Pilot users will get early access and exclusive pricing.' },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="pricing-page">
      <SEO title="Pricing" description="Simple, honest pricing. Start free with 200 credits/month. PI Check, PI Craft, Token Optimizer, Chrome Extension included. Pro and Max plans coming soon." path="/pricing" />
      <Navbar />

      <main className="pt-24 pb-16">
        <section className="max-w-7xl mx-auto px-6 md:px-12 text-center mb-16">
          <h1 className="font-space text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-4">
            Simple, honest pricing.<br /><span className="pi-gradient-text">Start free today.</span>
          </h1>
          <p className="text-white/50 text-base max-w-lg mx-auto">Pilot launch: 200 free credits/month. No credit card required.</p>
        </section>

        <section className="max-w-5xl mx-auto px-6 md:px-12 mb-24" data-testid="plans-section">
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div key={plan.id}
                className={`relative rounded-2xl p-6 transition-all ${plan.enabled ? 'hover:-translate-y-1' : 'opacity-70'} ${
                  plan.highlighted
                    ? 'pi-gradient-border bg-[var(--bg-elevated)]'
                    : 'bg-[var(--bg-elevated)] border border-white/[0.06] hover:border-white/[0.12]'
                }`}
                data-testid={`pricing-${plan.id}-plan`}>
                {plan.badge && (
                  <span className={`absolute -top-3 left-6 px-3 py-1 text-xs rounded-full font-bold text-white border-0 ${
                    plan.enabled ? 'shimmer-btn' : 'bg-white/10'
                  }`}>{plan.badge}</span>
                )}

                <div className="mb-6">
                  <h3 className="text-white font-space font-bold text-xl mb-1">{plan.name}</h3>
                  {plan.id === 'free' && <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider">Pilot Active</span>}
                </div>

                <div className="mb-6 flex items-baseline gap-1">
                  <span className="text-white font-space text-4xl font-bold">{plan.price}</span>
                  <span className="text-white/40 text-sm">{plan.period}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f.text} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.enabled ? 'text-emerald-400' : 'text-white/20'}`} />
                      <span className={plan.enabled ? 'text-white/60' : 'text-white/30'}>{f.text}</span>
                    </li>
                  ))}
                </ul>

                {plan.enabled ? (
                  <Link to={plan.ctaLink}>
                    <Button className="w-full shimmer-btn text-white rounded-full font-bold border-0 shadow-[0_4px_16px_rgba(124,58,237,0.3)]"
                      data-testid={`pricing-${plan.id}-cta`}>{plan.cta}</Button>
                  </Link>
                ) : (
                  <Button disabled className="w-full rounded-full bg-white/5 text-white/30 border border-white/5 cursor-not-allowed"
                    data-testid={`pricing-${plan.id}-cta`}>
                    <Lock className="w-3.5 h-3.5 mr-2" />
                    {plan.cta}
                  </Button>
                )}
              </div>
            ))}
          </div>
          <p className="text-[13px] text-[var(--mg)] text-center mt-6">
            Enterprise API from $99/month &middot; <a href="mailto:ai@sendpi.ai" className="text-[var(--pi-violet)] hover:underline">Contact for SDK pricing &rarr;</a>
          </p>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-6 md:px-12" data-testid="faq-section">
          <h2 className="font-space text-2xl sm:text-3xl text-white text-center mb-8 font-bold">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem key={faq.q} value={`faq-${faq.q.slice(0, 20)}`} className="border-white/[0.06] bg-[var(--bg-elevated)] rounded-xl px-6">
                <AccordionTrigger className="text-white hover:text-white/80 text-left font-space">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-white/45">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </main>

      <Footer />
    </div>
  );
}
